/**
 * Shop - Main shop container with routing between pages
 * Handles navigation between:
 * - ShopHome (landing with 3 entry points)
 * - UploadFlow (pet upload experience)
 * - InspirationGallery (filterable gallery)
 * - ProductCatalog (product browsing)
 * - ProductDetail (single product with customization)
 * - Checkout
 */
import React, { useState, useCallback } from 'react';
import ShopLayout from './ShopLayout';
import ShopHome from './pages/ShopHome';
import UploadFlow from './pages/UploadFlow';
import InspirationGallery from './pages/InspirationGallery';
import ProductCatalog from './pages/ProductCatalog';
import ProductDetail from './pages/ProductDetail';
import Checkout from './pages/Checkout';
import Account from './pages/Account';
import About from './pages/About';
import { CartProvider, useCart } from './context/CartContext';
import { AuthProvider, useAuth } from './context/AuthContext';
import CartSidebar from './components/CartSidebar';
import AuthModal from './components/AuthModal';
import { ToastProvider, useToast } from './components/Toast';

const ShopContent = () => {
  // Current page/route state
  const [currentPage, setCurrentPage] = useState('home');
  const [pageParams, setPageParams] = useState({});
  
  // Get cart from context
  const { totalItems, openCart, addItem } = useCart();
  
  // Get auth from context
  const { isLoggedIn, user, openLoginModal } = useAuth();
  
  // Get toast notifications
  const { showCart } = useToast();

  // Navigation handler (clears shop shortcuts when opening Products without category/scroll hints)
  const handleNavigate = useCallback((page, params = {}) => {
    setCurrentPage(page);
    setPageParams((prev) => {
      const next = { ...prev, ...params };
      if (page === 'products') {
        if (!Object.prototype.hasOwnProperty.call(params, 'shopCategory')) delete next.shopCategory;
        if (!Object.prototype.hasOwnProperty.call(params, 'scrollToProductId')) {
          delete next.scrollToProductId;
        }
      }
      return next;
    });
    window.scrollTo(0, 0);
  }, []);

  // Handle add to cart from product detail with toast notification
  const handleAddToCart = useCallback((item) => {
    addItem(item);
    showCart(`${item.name} added to cart!`, item.image);
  }, [addItem, showCart]);

  // Render current page
  const renderPage = () => {
    switch (currentPage) {
      case 'home':
        return <ShopHome onNavigate={handleNavigate} />;
      
      case 'upload':
        return (
          <UploadFlow 
            onNavigate={handleNavigate}
            onApplyToProducts={(image, petName) => {
              // Store the generated image for product customization
              setPageParams(prev => ({ ...prev, generatedImage: image, petName }));
              handleNavigate('products');
            }}
            preSelectedStyle={pageParams.preSelectedStyle}
            preSelectedBackground={pageParams.preSelectedBackground}
            preSelectedCostume={pageParams.preSelectedCostume}
          />
        );
      
      case 'gallery':
        return (
          <InspirationGallery 
            onNavigate={handleNavigate}
            onUseStyle={(styleInfo) => {
              // Store ALL style attributes to carry over to upload flow
              setPageParams(prev => ({ 
                ...prev, 
                preSelectedStyle: styleInfo.artStyle,
                preSelectedBackground: styleInfo.background,
                preSelectedCostume: styleInfo.costume,
              }));
            }}
          />
        );
      
      case 'products':
        return (
          <ProductCatalog 
            onNavigate={handleNavigate}
            onSelectProduct={(product) => {
              setPageParams(prev => ({ ...prev, selectedProduct: product }));
              handleNavigate('product-detail', { productId: product.id });
            }}
            generatedImage={pageParams.generatedImage}
            petName={pageParams.petName}
            initialCategory={pageParams.shopCategory || 'all'}
            scrollToProductId={pageParams.scrollToProductId || null}
          />
        );
      
      case 'product-detail':
        return (
          <ProductDetail
            productId={pageParams.productId}
            onNavigate={handleNavigate}
            onAddToCart={handleAddToCart}
            generatedImage={pageParams.generatedImage}
            petName={pageParams.petName}
            onGeneratePortrait={(image, name) => {
              // Persist portrait across all products
              setPageParams(prev => ({ ...prev, generatedImage: image, petName: name }));
            }}
          />
        );
      
      case 'checkout':
        return (
          <Checkout
            onNavigate={handleNavigate}
            onComplete={() => {
              // Order completed - could show confirmation, etc.
              handleNavigate('home');
            }}
          />
        );
      
      case 'account':
        return (
          <Account
            onNavigate={handleNavigate}
          />
        );

      case 'about':
        return <About onNavigate={handleNavigate} />;
      
      default:
        return <ShopHome onNavigate={handleNavigate} />;
    }
  };

  return (
    <>
      <ShopLayout
        cartItemCount={totalItems}
        onCartClick={openCart}
        onLogoClick={() => handleNavigate('home')}
        onNavigate={handleNavigate}
        currentPage={currentPage}
        isLoggedIn={isLoggedIn}
        user={user}
        onLoginClick={openLoginModal}
      >
        {renderPage()}
      </ShopLayout>
      
      {/* Cart Sidebar */}
      <CartSidebar onCheckout={() => handleNavigate('checkout')} />
      
      {/* Auth Modal */}
      <AuthModal />
    </>
  );
};

// Main Shop component with providers
const Shop = () => {
  return (
    <AuthProvider startLoggedIn={true}>
      <CartProvider>
        <ToastProvider>
          <ShopContent />
        </ToastProvider>
      </CartProvider>
    </AuthProvider>
  );
};

export default Shop;
